"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { formatPhoneForDisplay } from "@/lib/utils/phone";
import { type AuthFormAppearance, loginSubmitClass } from "./loginAppearance";
import { cn } from "@/lib/utils";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30;

interface AuthOtpFormProps {
  phone: string;
  onSubmit: (code: string) => void;
  onResendCode: () => Promise<void>;
  error?: string | null;
  isSubmitting?: boolean;
  appearance?: AuthFormAppearance;
}

export function AuthOtpForm({
  phone,
  onSubmit,
  onResendCode,
  error,
  isSubmitting = false,
  appearance = "default",
}: AuthOtpFormProps) {
  const t = useTranslations();
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      const char = value.replace(/\D/g, "").slice(-1);
      const next = [...digits];
      next[index] = char;
      setDigits(next);

      if (char && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      const code = next.join("");
      if (code.length === OTP_LENGTH && next.every((d) => d !== "")) {
        onSubmit(code);
      }
    },
    [digits, onSubmit],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [digits],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, OTP_LENGTH);
      if (!pasted) return;

      const next = Array(OTP_LENGTH).fill("");
      for (let i = 0; i < pasted.length; i++) {
        next[i] = pasted[i];
      }
      setDigits(next);

      const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
      inputRefs.current[focusIdx]?.focus();

      if (pasted.length === OTP_LENGTH) {
        onSubmit(pasted);
      }
    },
    [onSubmit],
  );

  const handleResend = async () => {
    setIsResending(true);
    try {
      await onResendCode();
      setCountdown(RESEND_COOLDOWN);
    } finally {
      setIsResending(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length === OTP_LENGTH) {
      onSubmit(code);
    }
  };

  const displayPhone = formatPhoneForDisplay(phone);

  return (
    <div className="space-y-5">
      <div className="p-3 bg-primary/5 text-center flex flex-col items-center gap-1 rounded-lg">
        <p className="text-sm opacity-90">{t("auth.otpSentTo")}</p>
        <p className="font-bold text-primary text-base" dir="ltr">
          {displayPhone}
        </p>
      </div>

      <form onSubmit={handleFormSubmit} className="space-y-5">
        <div
          className="flex items-center justify-center gap-2"
          dir="ltr"
          onPaste={handlePaste}
        >
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={isSubmitting}
              className={cn(
                "h-12 w-12 rounded-full border-2 border-input bg-transparent text-center text-lg font-bold transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50",
                appearance === "authPage" && "bg-white",
              )}
            />
          ))}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="text-center text-sm text-muted-foreground">
          <p>{t("auth.resendCodeHint")}</p>
          {countdown > 0 ? (
            <p className="mt-1 font-medium">
              {t("auth.resendCodeCountdown", { seconds: countdown })}
            </p>
          ) : (
            <button
              type="button"
              onClick={() => void handleResend()}
              disabled={isResending}
              className="mt-1 font-semibold text-primary hover:underline disabled:opacity-50"
            >
              {isResending ? (
                <Loader2 className="inline h-3.5 w-3.5 animate-spin" />
              ) : (
                t("auth.resendCode")
              )}
            </button>
          )}
        </div>

        <Button
          type="submit"
          className={cn(
            appearance === "authPage"
              ? loginSubmitClass(appearance, "continue")
              : "w-full h-12 rounded-xl font-bold",
          )}
          disabled={isSubmitting || digits.join("").length < OTP_LENGTH}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin me-2" />
              {t("common.loading")}
            </>
          ) : (
            t("auth.continue")
          )}
        </Button>
      </form>
    </div>
  );
}
